import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import PlanCard from "../components/PlanCard";
import { AuthContext } from "../context/AuthContext";
import { listPlans, setMyTier } from "../api/plansApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Pricing = () => {
  const { isAuthenticated, profile, refreshProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState("");
  const [error, setError] = useState("");

  const currentTier = profile?.tier || null;

  useEffect(() => {
    (async () => {
      try {
        setPlans(await listPlans());
      } catch {
        setError("Couldn't load plans. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const choose = async (plan) => {
    // Advisor / enterprise routes through sales rather than self-serve.
    if (plan.cta_type === "contact_sales") {
      navigate("/contact");
      return;
    }
    if (!isAuthenticated) {
      navigate("/login?next=/pricing");
      return;
    }
    setError("");
    setBusyTier(plan.tier_key);
    try {
      await setMyTier(plan.tier_key);
      await refreshProfile();
    } catch {
      setError("Couldn't update your plan. Please try again.");
    } finally {
      setBusyTier("");
    }
  };

  const ctaLabel = (plan) => {
    if (plan.cta_type === "contact_sales") return "Talk to sales";
    if (busyTier === plan.tier_key) return "Saving…";
    if (currentTier === plan.tier_key) return "Current plan";
    if (!isAuthenticated) return "Get started";
    return `Choose ${plan.name}`;
  };

  return (
    <div className="min-h-screen pt-28 bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white">
      <SEO
        path="/pricing"
        title="Pricing"
        description="Simple, transparent FinAI pricing. Start free and upgrade for deeper portfolio analysis, more AI advisor access, and premium market insights."
      />
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-[-0.02em]">
            Choose your plan
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-3 max-w-2xl mx-auto">
            Start free and upgrade as you grow. No payment is taken yet — switching
            tiers is instant while we finalize billing.
          </p>
        </div>

        {error && (
          <div className="mb-6 max-w-xl mx-auto px-4 py-3 rounded-xl bg-red-500/10 text-red-500 text-sm text-center">
            {error}
          </div>
        )}

        {loading ? (
          <Card className="text-center">Loading plans…</Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {plans.map((plan) => {
              const isCurrent =
                plan.cta_type !== "contact_sales" &&
                currentTier === plan.tier_key;
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrent={isCurrent}
                  busy={busyTier === plan.tier_key}
                  ctaLabel={ctaLabel(plan)}
                  onSelect={choose}
                />
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-gray-500 mt-8">
          Feature limits shown above are indicative and not yet enforced. You can
          switch plans anytime.
        </p>
      </div>
    </div>
  );
};

export default Pricing;
